
import React, { Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '@/components/Header';
import Hero from '@/components/Hero';
import SEOHead from '@/components/SEOHead';
import { LazyIntersection } from '@/components/LazyLoader';
import { useLanguage } from '@/contexts/LanguageContext';
// Import Features directly (above the fold) to prevent CLS
import Features from '@/components/Features';
import StaticSEOContent from '@/components/seo/StaticSEOContent';

// Lazy load below-fold components to reduce initial bundle and improve FCP
const DemoAccessBanner = lazy(() => import('@/components/DemoAccessBanner'));
const PlatformScreenshots = lazy(() => import('@/components/PlatformScreenshots'));
const Benefits = lazy(() => import('@/components/Benefits'));
const ValueProposition = lazy(() => import('@/components/ValueProposition'));
const Testimonials = lazy(() => import('@/components/Testimonials'));
const CTA = lazy(() => import('@/components/CTA'));
const Footer = lazy(() => import('@/components/Footer'));
const DocumentationSection = lazy(() => import('@/components/documentation/DocumentationSection'));
const IntegrationsShowcase = lazy(() => import('@/components/integrations/IntegrationsShowcase'));
const SEOContent = lazy(() => import('@/components/seo/SEOContent'));
const FeaturedArticles = lazy(() => import('@/components/FeaturedArticles'));
const LatestBlogArticles = lazy(() => import('@/components/LatestBlogArticles'));
const ComprehensiveSEOContent = lazy(() => import('@/components/seo/ComprehensiveSEOContent'));
const HomeFAQSection = lazy(() => import('@/components/seo/HomeFAQSection'));
const HomeVisibleContent = lazy(() => import('@/components/seo/HomeVisibleContent'));
const InstallAppSection = lazy(() => import('@/components/InstallAppSection'));

// Fixed height section skeleton to prevent CLS
const SectionSkeleton = () => (
  <div className="py-16 animate-pulse" style={{ minHeight: '500px' }}>
    <div className="max-w-7xl mx-auto px-4">
      <div className="h-8 bg-muted rounded w-1/3 mx-auto mb-8"></div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="h-48 bg-muted rounded"></div>
        <div className="h-48 bg-muted rounded"></div>
        <div className="h-48 bg-muted rounded"></div>
      </div>
    </div>
  </div>
);

const Index = () => {
  const { language } = useLanguage();
  
  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead 
        title={language === 'en'
          ? 'ProyectMind — AI Project Management | Free for Teams'
          : 'ProyectMind — Gestión de Proyectos con IA | Gratis'}
        description={language === 'en'
          ? 'Manage projects with AI. Gantt, Kanban, real-time collaboration. 100% free for teams worldwide.'
          : 'Gestiona proyectos con IA. Gantt, Kanban, colaboración en tiempo real. 100% gratis para México y LATAM.'}
        keywords={language === 'en'
          ? 'free project management, AI project management software, online Gantt chart, Monday alternative, Asana alternative, Kanban boards, task management'
          : 'gestión de proyectos gratis, software gestión proyectos IA México, diagrama Gantt online gratis, alternativa Monday gratis, alternativa Asana gratis, cronograma de actividades, tableros Kanban'}
        url="/"
      />
      
      {/* Static content for crawlers without JavaScript */}
      <StaticSEOContent pageType="home" />
      
      <Header />
      <main>
        <section id="hero">
          <Hero />
        </section>
        <Suspense fallback={<div className="h-32" />}>
          <DemoAccessBanner />
        </Suspense>
        <Suspense fallback={<SectionSkeleton />}>
          <InstallAppSection />
        </Suspense>
        <section id="features">
          <Features />
        </section>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <section id="platform-screenshots">
              <PlatformScreenshots />
            </section>
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <section id="benefits">
              <Benefits />
            </section>
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <section id="value">
              <ValueProposition />
            </section>
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <section id="integrations">
              <IntegrationsShowcase />
            </section>
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <DocumentationSection />
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <section id="articles">
              <FeaturedArticles />
            </section>
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <section id="testimonials">
              <Testimonials />
            </section>
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<div className="h-32" />}>
            <SEOContent />
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<div className="h-32" />}>
            <HomeVisibleContent />
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<div className="h-32" />}>
            <ComprehensiveSEOContent />
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<div className="h-32" />}>
            <HomeFAQSection />
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <section id="latest-blog">
              <LatestBlogArticles />
            </section>
          </Suspense>
        </LazyIntersection>
        <LazyIntersection rootMargin="200px">
          <Suspense fallback={<SectionSkeleton />}>
            <CTA />
          </Suspense>
        </LazyIntersection>
      </main>
      <Suspense fallback={<div className="h-32" />}>
        <Footer />
      </Suspense>
    </div>
  );
};

export default Index;
